import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';

interface PatientImportErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
  sessionId?: string;
}

/**
 * Error boundary wrapper for patient import pages.
 * Prevents rendering errors from crashing the entire page and reports them to Sentry.
 */
export const PatientImportErrorBoundary: React.FC<PatientImportErrorBoundaryProps> = ({
  children,
  componentName,
  sessionId,
}) => {
  return (
    <SentryErrorBoundary
      // eslint-disable-next-line @typescript-eslint/unbound-method
      fallback={({ error, resetError }) => (
        <PatientImportErrorFallback error={error} resetError={resetError} />
      )}
      beforeCapture={scope => {
        scope.setTag('component', componentName);
        scope.setTag('feature', 'patient-import');

        if (sessionId) {
          scope.setTag('session_id', sessionId);
          scope.setContext('import_session', { session_id: sessionId });
        }
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};

// =============================================================================
// Fallback UI
// =============================================================================

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

const PatientImportErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => resetError()}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Something went wrong</AlertTitle>
        <Typography variant="body2">
          Unable to display this page. Please try again or go back to the import
          list.
        </Typography>
        {process.env.NODE_ENV === 'development' && error instanceof Error && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}
          >
            {error.message}
          </Typography>
        )}
      </Alert>
      <Button
        variant="outlined"
        size="small"
        onClick={() => navigate('/patients/import')}
        sx={{ mt: 2 }}
      >
        Back to Import
      </Button>
    </Box>
  );
};
